<template>
  <div
    ref="panelRef"
    class="grid grid-cols-[60px_1px_1fr] bg-white border-gray-200 tablet-h:border tablet-h:border-gray-200/25 tablet-h:overflow-hidden tablet-h:rounded-xl"
    :class="side === 'infringing' ? 'border-t' : 'border-b'"
  >
    <!-- Time ruler (ticks) -->
    <div class="h-7" />
    <div class="h-7" />
    <div class="h-7 pt-1 pr-[6px]">
      <div class="relative">
        <div class="relative h-4">
          <span
            v-for="(label, i) in rulerLabels"
            :key="'l' + i"
            class="absolute top-0 whitespace-nowrap text-[12px] leading-[16px] text-gray-300 tabular-nums"
            :style="
              label.last
                ? { right: '0px' }
                : label.first
                  ? { left: '0px' }
                  : { left: label.pct + '%', transform: 'translateX(-50%)' }
            "
          >
            {{ label.text }}
          </span>
        </div>
        <div class="relative h-2">
          <span
            v-for="(tick, i) in rulerTicks"
            :key="'t' + i"
            class="absolute bottom-0 w-px bg-gray-300"
            :class="tick.tall ? 'h-2' : 'h-1'"
            :style="
              tick.pct <= 0
                ? { left: '0px' }
                : tick.pct >= 100
                  ? { right: '0px' }
                  : { left: tick.pct + '%', transform: 'translateX(-50%)' }
            "
          />
        </div>
      </div>
    </div>

    <!-- Transcript row -->
    <template v-if="showTranscript">
      <div class="flex items-center justify-center h-[68px]">
        <TextIcon class="size-5 text-gray-800" />
      </div>
      <div />
      <div class="h-[68px] pr-[6px] overflow-hidden">
        <div ref="transcriptRef" class="relative flex items-center h-full">
          <div class="flex gap-x-1 w-full">
            <div
              v-for="(text, n) in visibleTranscript"
              :key="n"
              class="flex-1 min-w-0 h-[56px] bg-gray-200/25 rounded pt-[7px] pl-[7px] pr-[3px]"
            >
              <div
                class="text-[8px] leading-[10px] text-gray-500 h-[48px] overflow-hidden"
              >
                {{ text }}
              </div>
            </div>
          </div>
          <MatchHighlights :matches="transcriptMatches" :duration="duration" />
        </div>
      </div>

      <div class="col-span-3 h-px bg-gray-200/25" />
    </template>

    <!-- Audio row -->
    <div class="flex items-center justify-center h-[68px]">
      <VocalAndInstrumentalIcon class="size-5 text-gray-800" />
    </div>
    <div />
    <div class="h-[68px] pr-[6px]">
      <div class="relative flex items-center h-full">
        <div class="w-full h-[42px]">
          <Wave v-if="audioUrl" :url="audioUrl" type="original" />
        </div>
        <MatchHighlights :matches="audioMatches" :duration="duration" />
      </div>
    </div>

    <!-- Video row -->
    <template v-if="showVideo">
      <div class="col-span-3 h-px bg-gray-200/25" />

      <div class="flex items-center justify-center h-[68px]">
        <EyeOpenIcon class="size-5 text-gray-800" />
      </div>
      <div />
      <div class="h-[68px] pr-[6px] overflow-hidden">
        <div ref="framesRef" class="relative flex items-center h-full">
          <div class="relative w-full h-[56px] overflow-hidden rounded-[4px]">
            <div class="flex w-full h-full gap-x-px">
              <img
                v-if="!framesReady && frames.length"
                :src="frames[0]"
                class="h-[56px] invisible absolute"
                @load="onFrameLoad"
              />
              <template
                v-for="(frameUrl, index) in distributedFrames"
                :key="index"
              >
                <div class="relative h-[56px] flex-1 min-w-0">
                  <Skeleton class="absolute inset-0" />
                  <img
                    :src="frameUrl"
                    loading="lazy"
                    decoding="async"
                    class="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-300"
                    @load="
                      ($event.target as HTMLImageElement).classList.replace(
                        'opacity-0',
                        'opacity-100'
                      )
                    "
                  />
                </div>
              </template>
              <template v-if="!framesReady">
                <Skeleton
                  v-for="n in 9"
                  :key="'sk-' + n"
                  class="h-[56px] flex-1"
                />
              </template>
            </div>
            <div class="absolute inset-0 bg-[#E7E9EC]/80 pointer-events-none" />
          </div>
          <MatchHighlights :matches="videoMatches" :duration="duration" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import Wave from '@/features/copyright/components/Wave.vue'
import { TextIcon, VocalAndInstrumentalIcon, EyeOpenIcon } from '@dacr/icons'
import { Skeleton } from '@dacr/library'
import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue'
import MatchHighlights from './MatchHighlights.vue'
import type { TimelineMatch } from './timeline-types'

const props = defineProps<{
  duration: number
  audioUrl?: string | null
  transcript?: readonly string[]
  frames?: readonly string[]
  matches?: readonly TimelineMatch[]
  side: 'registered' | 'infringing'
}>()

const panelRef = ref<HTMLElement>()
const transcriptRef = ref<HTMLElement>()
const framesRef = ref<HTMLElement>()

const panelWidth = ref(0)
const transcriptWidth = ref(0)
const framesWidth = ref(0)
const frameWidth = ref(0)

const CARD_WIDTH = 83
const CARD_GAP = 4
const FRAME_HEIGHT = 56
const FRAME_WIDTH = 99

const transcript = computed(() => props.transcript ?? [])
const frames = computed(() => props.frames ?? [])
const matches = computed(() => props.matches ?? [])

const showTranscript = computed(() => transcript.value.length > 0)
const showVideo = computed(() => frames.value.length > 0)

const sideMatches = (
  type: 'Audio' | 'Video' | 'Transcript' | 'Text' | 'Image'
) =>
  matches.value
    .filter((m) => m.type === type)
    .map((m) => ({
      id: m.id,
      start: m[props.side].start,
      end: m[props.side].end,
      level: m.level,
      match: m,
    }))

const audioMatches = computed(() => sideMatches('Audio'))
const videoMatches = computed(() => sideMatches('Video'))
const transcriptMatches = computed(() => [
  ...sideMatches('Transcript'),
  ...sideMatches('Text'),
])

const visibleCardCount = computed(() => {
  const available = transcriptWidth.value
  if (available <= 0) return 0
  const max = Math.floor((available + CARD_GAP) / (CARD_WIDTH + CARD_GAP))
  return Math.max(1, Math.min(transcript.value.length, max))
})

const visibleTranscript = computed(() =>
  transcript.value.slice(0, visibleCardCount.value)
)

const framesReady = computed(
  () => framesWidth.value > 0 && frameWidth.value > 0
)

const onFrameLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    frameWidth.value = Math.round(
      (FRAME_HEIGHT / img.naturalHeight) * img.naturalWidth
    )
  }
}

const frameCount = computed(() => {
  if (!framesReady.value) return 0
  const w = frameWidth.value || FRAME_WIDTH
  return Math.max(2, Math.round(framesWidth.value / w))
})

const distributedFrames = computed(() => {
  const all = frames.value
  if (!framesReady.value || !all.length) return []
  const n = frameCount.value
  if (all.length <= n) return [...all]

  const result: string[] = [all[0]]
  for (let i = 1; i < n - 1; i++) {
    result.push(all[Math.round((i * (all.length - 1)) / (n - 1))])
  }
  result.push(all[all.length - 1])
  return result
})

const formatTime = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds))
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

const labelCount = computed(() => {
  const contentWidth = panelWidth.value - 67
  if (contentWidth <= 0 || !props.duration) return 0
  return Math.max(2, Math.min(12, Math.floor(contentWidth / 110)))
})

const rulerLabels = computed(() => {
  const n = labelCount.value
  if (n < 2) return []
  const out: Array<{
    text: string
    pct: number
    first: boolean
    last: boolean
  }> = []
  for (let i = 0; i < n; i++) {
    const frac = i / (n - 1)
    out.push({
      text: formatTime(frac * props.duration),
      pct: frac * 100,
      first: i === 0,
      last: i === n - 1,
    })
  }
  return out
})

const rulerTicks = computed(() => {
  const n = labelCount.value
  if (n < 2) return []
  const tallCount = n * 2 - 1
  const out: Array<{ pct: number; tall: boolean }> = []
  for (let j = 0; j < tallCount; j++) {
    out.push({ pct: (j / (tallCount - 1)) * 100, tall: true })
    if (j < tallCount - 1) {
      for (let k = 1; k <= 4; k++) {
        out.push({ pct: ((j + k / 5) / (tallCount - 1)) * 100, tall: false })
      }
    }
  }
  return out
})

const observe = (el: HTMLElement | undefined, setter: (w: number) => void) => {
  if (!el) return null
  const ro = new ResizeObserver(([entry]) => {
    setter(entry.contentRect.width)
  })
  ro.observe(el)
  return ro
}

let observers: Array<ResizeObserver | null> = []

onMounted(async () => {
  await nextTick()
  if (panelRef.value)
    panelWidth.value = panelRef.value.getBoundingClientRect().width
  if (transcriptRef.value)
    transcriptWidth.value = transcriptRef.value.getBoundingClientRect().width
  if (framesRef.value)
    framesWidth.value = framesRef.value.getBoundingClientRect().width

  observers = [
    observe(panelRef.value, (w) => (panelWidth.value = w)),
    observe(transcriptRef.value, (w) => (transcriptWidth.value = w)),
    observe(framesRef.value, (w) => (framesWidth.value = w)),
  ]
})

onUnmounted(() => {
  observers.forEach((o) => o?.disconnect())
})

watch(
  () => frames.value[0],
  () => {
    frameWidth.value = 0
  }
)
</script>
